<template>
  <div class="page-container">
    <!-- Inventory Request Table -->
    <div class="mb-8 border border-[#e0e0e0] overflow-x-auto" v-if="isModuleVisible('base_management')">
      <div class="d-flex justify-space-between align-center p-4">
        <span class="text-lg font-semibold">Inventory Requests</span>
        <div class="d-flex">
          <v-text-field
            v-model="inventoryFilters.complaint"
            label="Complaint/Request No."
            solo
            flat
            clearable
            @input="fetchInventoryRequests"
            outlined
            dense
            hide-details
            class="mr-2"
            style="width: 250px"
          ></v-text-field>
          <v-select
            v-model="inventoryFilters.status"
            label="Select Status"
            :items="requestStatusOptions"
            item-value="value"
            item-text="label"
            solo
            flat
            clearable
            @change="fetchInventoryRequests"
            outlined
            dense
            hide-details
            class="mr-2"
            style="width: 200px"
          ></v-select>
        </div>
      </div>
      <v-data-table
        :headers="headers"
        :items="inventoryRequests"
        class="table-radius"
        :options.sync="inventoryOptions"
        :server-items-length="inventoryTotalItems"
        :loading="loadingInventory"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        show-expand
        item-key="_id"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        height="auto"
        fixed-header
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="`tooltip-${index}`"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:[`item.data-table-expand`]="{ item, expand, isExpanded }">
          <td v-if="item.requestType === 'INVENTORY_REQUEST'" class="text-start">
            <v-btn
              icon
              @click="expand(!isExpanded)"
              class="v-data-table__expand-icon"
              :class="{ 'v-data-table__expand-icon--active': isExpanded }"
            >
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </td>
        </template>
        <template v-slot:[`item.requestedBy`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.raisedBy?.firstName }} {{ item.raisedBy?.lastName }}
            <br />
            {{ $dayjs(item.raisedOn).format('DD/MM/YYYY') }}
          </td>
        </template>
        <template v-slot:[`item.requestType`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.requestType.split('_').join(' ') }}
          </td>
        </template>
        <template v-slot:[`item.requestedId`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.requestId || '-' }}
          </td>
        </template>
        <template v-slot:[`item.complaintNumber`]="{ item }">
          <td
            class="px-4 py-4 table-font cursor-pointer hover:text-blue-600"
            @click="() => navToComplaint(item.complaint?.complainSrNo)"
          >
            <span>
              {{ item.complaint?.complainSrNo || '-' }}
            </span>
          </td>
        </template>
        <template v-slot:[`item.comment`]="{ item }">
          <td class="px-4 table-font">
            <div class="flex items-center gap-2">
              <p class="ellipsisClass w-32 mb-0">
                {{ item.comments[item.comments.length - 1]?.comment }}
              </p>
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltipOn }">
                  <v-icon
                    v-on="{ ...tooltipOn }"
                    @click.stop="toggleMenu(item._id, item.comments)"
                    v-if="item.comments.length"
                    style="font-size: 20px"
                    color="#fb8c00"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>See Comments</span>
              </v-tooltip>

              <CommentsPopOver
                v-if="menuState[item._id]"
                :comments="selectedComments[item._id]"
                :menuState="menuState[item._id]"
                @close="closeMenu(item._id)"
              ></CommentsPopOver>
            </div>
          </td>
        </template>
        <template v-slot:[`item.approvedBy`]="{ item }">
          <td class="px-4 table-font">
            {{ item.approvals[item.approvals.length - 1]?.approvedBy?.firstName }}
            {{ item.approvals[item.approvals.length - 1]?.approvedBy?.lastName || '-' }}
            <br />
            {{
              item.approvals && item.approvals.length > 0
                ? $dayjs(item.approvals[0]?.approvedOn).format('DD/MM/YYYY')
                : ''
            }}
          </td>
        </template>
        <template v-slot:[`item.rejectedBy`]="{ item }">
          <td class="px-4 table-font">
            {{ item.rejectedBy?.firstName }}
            {{ item.rejectedBy?.lastName || '-' }}
            <br />
            {{ item.rejectedBy ? $dayjs(item.rejectedOn).format('DD/MM/YYYY') : '' }}
          </td>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <td class="w-48 td_font">
            <v-menu offset-y :disabled="isStatusDisabled(item.requestStatus)">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  :style="getStatusColor(item.requestStatus)"
                  class="text-capitalize"
                  >{{ item.requestStatus }}</v-btn
                >
              </template>
              <v-list
                style="
                  max-height: 200px;
                  overflow-y: auto;
                  width: 250px;
                  border-radius: 20px;
                  text-align: center;
                "
              >
                <v-list-item
                  v-for="status in getRequestStatuses(item)"
                  :key="status.value"
                  @click="addComment(item, status.value)"
                >
                  <v-list-item-content :style="getStatusColor(status.value)">
                    <span style="white-space: nowrap; font-size: 14px">{{ status.label }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </template>

        <template v-slot:[`expanded-item`]="{ headers, item }">
          <td :colspan="headers.length">
            <v-data-table
              v-if="item.products && item.products.length > 0"
              class="expanded-table bg-gray-100 dark-bg-default p-7"
              :headers="getExpandedHeaders(item)"
              :items="item.products"
              hide-default-footer
            >
              <template v-slot:[`item.productName`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.productName }}</td>
              </template>
              <template v-slot:[`item.modelNumber`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.modelNumber }}</td>
              </template>
              <template v-slot:[`item.storeName`]="{ item }">
                <td class="px-4 table-font">{{ item.store?.storeName }}</td>
              </template>
              <template v-slot:[`item.quantityRequested`]="{ item }">
                <td class="px-4 table-font">{{ item.quantityRequested }}</td>
              </template>
              <template v-slot:[`item.productPrice`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.productPrice }}</td>
              </template>
            </v-data-table>
            <v-container v-else>
              <v-row class="flex justify-center my-2">
                <v-btn
                  @click="raiseRequest(item)"
                  :color="$vuetify.theme.currentTheme.primary"
                  class="white--text"
                  v-if="addPartButton(item)"
                  >Add Parts</v-btn
                >
                <p v-else>You can not add parts for {{ item.requestStatus }} request.</p>
              </v-row>
            </v-container>
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Complaint Approval Request Table -->
    <div class="mb-8 border border-[#e0e0e0] overflow-x-auto" v-if="isModuleVisible('base_management')">
      <div class="d-flex justify-space-between align-center p-4">
        <span class="text-lg font-semibold">Complaint Approval Requests</span>
        <div class="d-flex">
          <v-text-field
            v-model="complaintFilters.complaint"
            label="Complaint/Request No."
            solo
            flat
            clearable
            @input="fetchComplaintRequests"
            outlined
            dense
            hide-details
            class="mr-2"
            style="width: 250px"
          ></v-text-field>
          <v-select
            v-model="complaintFilters.status"
            label="Select Status"
            :items="requestStatusOptions"
            item-value="value"
            item-text="label"
            solo
            flat
            clearable
            @change="fetchComplaintRequests"
            outlined
            dense
            hide-details
            class="mr-2"
            style="width: 200px"
          ></v-select>
        </div>
      </div>
      <v-data-table
        :headers="headers"
        :items="complaintRequests"
        class="table-radius"
        :options.sync="complaintOptions"
        :server-items-length="complaintTotalItems"
        :loading="loadingComplaint"
        :single-expand="singleExpand"
        :expanded.sync="expanded"
        show-expand
        item-key="_id"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="`tooltip-${index}`"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="`normal-${index}`" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:[`item.data-table-expand`]="{ item, expand, isExpanded }">
          <td v-if="item.requestType === 'COMPLAINT_APPROVAL_REQUEST'" class="text-start">
            <v-btn
              icon
              @click="expand(!isExpanded)"
              class="v-data-table__expand-icon"
              :class="{ 'v-data-table__expand-icon--active': isExpanded }"
            >
              <v-icon>mdi-chevron-down</v-icon>
            </v-btn>
          </td>
        </template>
        <template v-slot:[`item.requestedBy`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.raisedBy?.firstName }} {{ item.raisedBy?.lastName }}
            <br />
            {{ $dayjs(item.raisedOn).format('DD/MM/YYYY') }}
          </td>
        </template>
        <template v-slot:[`item.requestType`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.requestType.split('_').join(' ') }}
          </td>
        </template>
        <template v-slot:[`item.requestedId`]="{ item }">
          <td class="px-4 py-4 table-font">
            {{ item.requestId || '-' }}
          </td>
        </template>
        <template v-slot:[`item.complaintNumber`]="{ item }">
          <td
            class="px-4 py-4 table-font cursor-pointer hover:text-blue-600"
            @click="() => navToComplaint(item.complaint?.complainSrNo)"
          >
            <span>
              {{ item.complaint?.complainSrNo || '-' }}
            </span>
          </td>
        </template>
        <template v-slot:[`item.comment`]="{ item }">
          <td class="px-4 table-font">
            <div class="flex items-center gap-2">
              <p class="ellipsisClass w-32 mb-0">
                {{ item.comments[item.comments.length - 1]?.comment }}
              </p>
              <v-tooltip top>
                <template v-slot:activator="{ on: tooltipOn }">
                  <v-icon
                    v-on="{ ...tooltipOn }"
                    @click.stop="toggleMenu(item._id, item.comments)"
                    v-if="item.comments.length"
                    style="font-size: 20px"
                    color="#fb8c00"
                  >
                    mdi-information
                  </v-icon>
                </template>
                <span>See Comments</span>
              </v-tooltip>

              <CommentsPopOver
                v-if="menuState[item._id]"
                :comments="selectedComments[item._id]"
                :menuState="menuState[item._id]"
                @close="closeMenu(item._id)"
              ></CommentsPopOver>
            </div>
          </td>
        </template>
        <template v-slot:[`item.approvedBy`]="{ item }">
          <td class="px-4 table-font">
            {{ item.approvals[item.approvals.length - 1]?.approvedBy?.firstName }}
            {{ item.approvals[item.approvals.length - 1]?.approvedBy?.lastName || '-' }}
            <br />
            {{
              item.approvals && item.approvals.length > 0
                ? $dayjs(item.approvals[0]?.approvedOn).format('DD/MM/YYYY')
                : ''
            }}
          </td>
        </template>
        <template v-slot:[`item.rejectedBy`]="{ item }">
          <td class="px-4 table-font">
            {{ item.rejectedBy?.firstName }}
            {{ item.rejectedBy?.lastName || '-' }}
            <br />
            {{ item.rejectedBy ? $dayjs(item.rejectedOn).format('DD/MM/YYYY') : '' }}
          </td>
        </template>

        <template v-slot:[`item.status`]="{ item }">
          <td class="w-48 td_font">
            <v-menu offset-y :disabled="isStatusDisabled(item.requestStatus)">
              <template v-slot:activator="{ on }">
                <v-btn
                  v-on="on"
                  :style="getStatusColor(item.requestStatus)"
                  class="text-capitalize"
                  >{{ item.requestStatus }}</v-btn
                >
              </template>
              <v-list
                style="
                  max-height: 200px;
                  overflow-y: auto;
                  width: 250px;
                  border-radius: 20px;
                  text-align: center;
                "
              >
                <v-list-item
                  v-for="status in getRequestStatuses(item)"
                  :key="status.value"
                  @click="addComment(item, status.value)"
                >
                  <v-list-item-content :style="getStatusColor(status.value)">
                    <span style="white-space: nowrap; font-size: 14px">{{ status.label }}</span>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </template>

        <template v-slot:[`expanded-item`]="{ headers, item }">
          <td :colspan="headers.length">
            <v-data-table
              v-if="item.products && item.products.length > 0"
              class="expanded-table bg-gray-100 dark-bg-default p-7"
              :headers="getExpandedHeaders(item)"
              :items="item.products"
              hide-default-footer
            >
              <template v-slot:[`item.productName`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.productName }}</td>
              </template>
              <template v-slot:[`item.modelNumber`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.modelNumber }}</td>
              </template>
              <template v-slot:[`item.storeName`]="{ item }">
                <td class="px-4 table-font">{{ item.store?.storeName }}</td>
              </template>
              <template v-slot:[`item.quantityRequested`]="{ item }">
                <td class="px-4 table-font">{{ item.quantityRequested }}</td>
              </template>
              <template v-slot:[`item.productPrice`]="{ item }">
                <td class="px-4 table-font">{{ item.product?.productPrice }}</td>
              </template>
            </v-data-table>
            <v-container v-else>
              <v-row class="flex justify-center my-2">
                <v-btn
                  @click="raiseRequest(item)"
                  :color="$vuetify.theme.currentTheme.primary"
                  class="white--text"
                  v-if="addPartButton(item)"
                  >Add Parts</v-btn
                >
                <p v-else>You can not add parts for {{ item.requestStatus }} request.</p>
              </v-row>
            </v-container>
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Leave Request Table -->
    <div class="mb-8 border border-[#e0e0e0]">
      <div class="d-flex justify-space-between align-center p-4">
        <span class="text-lg font-semibold">Leave Requests</span>
        <div class="d-flex">
          <v-select
            v-model="leaveFilters.status"
            label="Select Status"
            :items="requestStatusOptions"
            item-value="value"
            item-text="label"
            solo
            flat
            clearable
            @change="fetchLeaveRequestsMain"
            outlined
            dense
            hide-details
            class="mr-2"
            style="width: 200px"
          ></v-select>
        </div>
      </div>
      <LeaveRequestTable
        :leaveRequests="leaveRequests"
        :totalLeaveRequests="totalLeaveRequests"
        :loading="loadingLeave"
        @statusUpdated="fetchLeaveRequestsMain"
        @refetchPage="refetchPage"
      />
    </div>

    <!-- Reimbursement Table -->
    <div class="mb-8 border border-[#e0e0e0]" v-if="isModuleVisible('CLAIMS')">
      <ReimbursementTable
        :reimbursements="reimbursements"
        :loading="loadingReimbursement"
        @statusUpdated="statusUpdated"
      />
    </div>

    <v-dialog v-model="openCommentDialog" max-width="500" persistent>
      <v-card>
        <v-card-title class="text-h5 flex justify-between items-center">
          Add Comment <v-icon @click="handleChipCancel" class="cursor-pointer">mdi-close</v-icon>
        </v-card-title>
        <div class="px-4 py-2">
          <v-select
            dense
            outlined
            v-model="comment"
            :items="getCommentOptions()"
            label="Comment"
            hide-details
            class="mb-3"
          ></v-select>
        </div>
        <div class="px-4">
          <v-text-field
            dense
            outlined
            v-model="orderNumber"
            label="Order Number"
            hide-details
            class="mb-3"
          ></v-text-field>
        </div>
        <v-card-actions class="flex justify-end mb-2">
          <v-btn @click="handleChipCancel" class="rounded-md w-24" outlined>Cancel</v-btn>
          <v-btn
            @click="handleChipClick"
            class="white--text w-24 rounded-md"
            :color="$vuetify.theme.currentTheme.primary"
            >Add</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="requestDialog" max-width="63rem">
      <div>
        <InventoryRequest
          :editData="editData"
          @requestRaised="onRaiseRequest"
          @cancel="requestDialog = false"
        />
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { hasPermission, isModuleVisible, isAnyFeatureAllowed } from '@/utils/common'
import permissionData from '@/utils/permissions'
import InventoryRequest from '../InventoryRequest'
import LeaveRequestTable from './LeaveRequestTable'
import { sparePartRequestStatusColors } from '@/utils/workforce/statusColors'
import CommentsPopOver from '../CommentsPopOver.vue'
import ReimbursementTable from './ReimbursementTable.vue'

const predefinedComments = {
  APPROVED: ['Inventory request Approved.'],
  HOLD: [
    'Inventory request Hold, due to insufficient stock.',
    'Inventory request Hold, due to gathering approvals.',
    'Inventory request Hold, waiting for refill and notify back.'
  ],
  REJECTED: [
    'Inventory request Rejected, due to product model discontinue.',
    'Inventory request Rejected, due to lack of approvals.',
    'Inventory request Rejected, due to not available in requested store but available in another store.'
  ],
  WITHDRAW: [
    'Inventory request Withdraw, due to customer not required service any more.',
    ' Inventory request Withdraw, request raised by mistake for this complaint.'
  ]
}

export default {
  name: 'AlertComponent',
  layout: 'Workforce/default',
  components: {
    InventoryRequest,
    LeaveRequestTable,
    CommentsPopOver,
    ReimbursementTable
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      headers: [
        { text: 'Requested By', value: 'requestedBy', sortable: false },
        { text: 'Request Type', value: 'requestType', sortable: false },
        { text: 'Requested Id', value: 'requestedId', sortable: false },
        {
          text: 'Related Comaplaint',
          value: 'complaintNumber',
          sortable: false
        },
        { text: 'Comment', value: 'comment', sortable: false },
        { text: 'Approved By', value: 'approvedBy', sortable: false },
        { text: 'Rejected By', value: 'rejectedBy', sortable: false },
        { text: 'Status', value: 'status', sortable: false }
      ],
      requestStatusOptions: [
        { value: '', label: 'All' },
        { value: 'RAISED', label: 'Raised' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' },
        { value: 'HOLD', label: 'Hold' },
        { value: 'WITHDRAW', label: 'Withdraw' }
      ],
      // Filters for each table
      inventoryFilters: {
        complaint: '',
        status: ''
      },
      complaintFilters: {
        complaint: '',
        status: ''
      },
      leaveFilters: {
        status: ''
      },
      // Data for each table
      inventoryRequests: [],
      complaintRequests: [],
      leaveRequests: [],
      reimbursements: [],
      editData: null,
      comment: '',
      orderNumber: '',
      // Options for pagination
      inventoryOptions: { itemsPerPage: 5, page: 1 },
      complaintOptions: { itemsPerPage: 5, page: 1 },
      leaveOptions: { itemsPerPage: 5, page: 1 },
      // Total counts for pagination
      inventoryTotalItems: 0,
      complaintTotalItems: 0,
      totalLeaveRequests: 0,
      // Loading states
      loadingInventory: false,
      loadingComplaint: false,
      loadingLeave: false,
      loadingReimbursement: false,
      loadingRefresh: false,
      menuState: {},
      selectedComments: {},
      singleExpand: true,
      expanded: [],
      selectedRequestId: null,
      selectedRequestStatus: '',
      openCommentDialog: false,
      requestDialog: false,
      requestStatusClrs: sparePartRequestStatusColors,
      comments: predefinedComments
    }
  },
  watch: {
    inventoryOptions: {
      handler() {
        this.fetchInventoryRequests()
      },
      deep: true
    },
    complaintOptions: {
      handler() {
        this.fetchComplaintRequests()
      },
      deep: true
    },
    leaveOptions: {
      handler() {
        this.fetchLeaveRequestsMain()
      },
      deep: true
    }
  },
  computed: {
    canEdit() {
      return hasPermission(this.userData, permissionData.requestWrite)
    },
    isModuleVisible() {
      return isModuleVisible
    },
    isAnyFeatureAllowed() {
      return isAnyFeatureAllowed
    }
  },
  mounted() {
    this.fetchInventoryRequests()
    this.fetchComplaintRequests()
    this.fetchLeaveRequestsMain()
    this.fetchReimbursementData()
  },
  methods: {
    async statusUpdated() {
      console.log('asasa')

      await this.fetchReimbursementData()
    },
    async fetchReimbursementData() {
      this.loadingReimbursement = true
      try {
        const response = await this.$axios.get('/orgSetup/reimbursement')
        this.reimbursements = response?.data?.reimbursements?.data || []
      } catch (error) {
        console.log(error)
        this.reimbursements = []
      } finally {
        this.loadingReimbursement = false
      }
    },
    toggleMenu(itemId, comments) {
      Object.keys(this.menuState).forEach((key) => {
        if (key !== itemId) {
          this.$set(this.menuState, key, false)
        }
      })
      this.$set(this.menuState, itemId, !this.menuState[itemId])

      if (this.menuState[itemId]) {
        this.$set(this.selectedComments, itemId, comments)
      } else {
        this.$delete(this.selectedComments, itemId)
      }
    },
    closeMenu(id) {
      this.$set(this.menuState, id, false)
    },
    getExpandedHeaders() {
      return [
        { text: 'Product Name', value: 'productName', sortable: false },
        { text: 'Model Number', value: 'modelNumber', sortable: false },
        {
          text: 'Quantity Requested',
          value: 'quantityRequested',
          sortable: false
        },
        { text: 'Store Name', value: 'storeName', sortable: false },
        { text: 'Price', value: 'productPrice', sortable: false }
      ]
    },
    getCommentOptions() {
      return predefinedComments[this.selectedRequestStatus]
    },
    navToComplaint(id) {
      const routeData = this.$router.resolve({
        path: '/workforce/leadspage/inventory',
        query: { active_tab: 'complaint', complaint_no: id }
      })
      window.open(routeData.href, '_blank')
    },
    getRequestStatuses(item) {
      const loggedInUser = JSON.parse(localStorage.getItem('user'))
      const userId = loggedInUser._id
      if (item.raisedBy?._id === userId) {
        return [
          {
            label: 'Withdraw',
            value: 'WITHDRAW'
          }
        ]
      }

      const statuses = [
        {
          label: 'Approve',
          value: 'APPROVED'
        },
        {
          label: 'Reject',
          value: 'REJECTED'
        },
        {
          label: 'Hold',
          value: 'HOLD'
        },
        {
          label: 'Withdraw',
          value: 'WITHDRAW'
        }
      ]

      return statuses
    },
    async fetchInventoryRequests() {
      try {
        this.loadingInventory = true
        const { page, itemsPerPage } = this.inventoryOptions
        const params = {
          page,
          limit: itemsPerPage,
          requestType: 'INVENTORY_REQUEST'
        }

        if (this.inventoryFilters.status) {
          params.status = this.inventoryFilters.status
        }
        if (this.inventoryFilters.complaint) {
          params.search = this.inventoryFilters.complaint.trim()
        }

        const response = await this.$axios.get('/ecom/inventory/requests', {
          params
        })
        this.inventoryRequests = response.data?.requests || []
        this.inventoryRequests.forEach((item) => {
          if (this.menuState[item._id]) {
            this.$set(this.selectedComments, item._id, item.comments)
          }
        })
        this.inventoryTotalItems = response.data?.pagination?.totalCount || 0
        this.loadingInventory = false
      } catch (error) {
        console.error('An error occurred:', error)
        this.inventoryRequests = []
        this.inventoryTotalItems = 0
        this.loadingInventory = false
      }
    },
    async fetchComplaintRequests() {
      try {
        this.loadingComplaint = true
        const { page, itemsPerPage } = this.complaintOptions
        const params = {
          page,
          limit: itemsPerPage,
          requestType: 'COMPLAINT_APPROVAL_REQUEST'
        }

        if (this.complaintFilters.status) {
          params.status = this.complaintFilters.status
        }
        if (this.complaintFilters.complaint) {
          params.search = this.complaintFilters.complaint.trim()
        }

        const response = await this.$axios.get('/ecom/inventory/requests', {
          params
        })
        this.complaintRequests = response.data?.requests || []
        this.complaintRequests.forEach((item) => {
          if (this.menuState[item._id]) {
            this.$set(this.selectedComments, item._id, item.comments)
          }
        })
        this.complaintTotalItems = response.data?.pagination?.totalCount || 0
        this.loadingComplaint = false
      } catch (error) {
        console.error('An error occurred:', error)
        this.complaintRequests = []
        this.complaintTotalItems = 0
        this.loadingComplaint = false
      }
    },
    async fetchLeaveRequestsMain() {
      this.loadingLeave = true
      try {
        const loggedInUser = JSON.parse(localStorage.getItem('user'))
        const { page, itemsPerPage } = this.leaveOptions
        const userId = loggedInUser._id
        const params = {
          leaveStatus: this.leaveFilters.status,
          page,
          limit: itemsPerPage
        }
        const response = await this.$axios.get(`/emp/leave/request/${userId}`, {
          params
        })
        this.leaveRequests = response.data?.employeeLeaveRequests || []
        this.totalLeaveRequests = response.data?.pagination?.totalCount || 0
      } catch (error) {
        console.error('Error fetching leave requests:', error)
        this.leaveRequests = []
        this.totalLeaveRequests = 0
      } finally {
        this.loadingLeave = false
      }
    },
    addComment(item, status) {
      this.openCommentDialog = true
      this.selectedRequestId = item._id
      this.selectedRequestStatus = status
    },
    handleChipCancel() {
      this.openCommentDialog = false
      this.selectedRequestId = null
      this.selectedRequestStatus = ''
      this.comment = ''
    },
    async handleChipClick() {
      try {
        if (this.selectedRequestStatus === 'REJECTED' && !this.orderNumber) {
          this.$toast.error('Please fill the order number')
          return
        }
        const requestId = this.selectedRequestId
        const payload = {
          status: this.selectedRequestStatus,
          comment: this.comment,
          orderNumber: this.orderNumber
        }
        await this.$axios.put(`/ecom/inventory/request/${requestId}`, payload)
        // Refresh all tables
        this.fetchInventoryRequests()
        this.fetchComplaintRequests()
        this.fetchLeaveRequestsMain()
        this.handleChipCancel()
      } catch (error) {
        console.log(error)
      }
    },
    isStatusDisabled(status) {
      const disabledStatuses = ['APPROVED', 'REJECTED', 'WITHDRAW']

      return disabledStatuses.includes(status)
    },
    getStatusColor(taskStatus) {
      const colorInfo = this.requestStatusClrs[taskStatus]
      return {
        color: colorInfo ? colorInfo.textColor : 'black',
        backgroundColor: colorInfo ? colorInfo.bgColor : 'white',
        height: '39px',
        borderRadius: '20px',
        padding: '5px 10px',
        fontSize: '14px',
        boxShadow: 'none',
        border: '1px solid transparent'
      }
    },
    raiseRequest(item) {
      this.requestDialog = true
      this.editData = item
    },
    async onRaiseRequest() {
      await this.fetchInventoryRequests()
      this.requestDialog = false
    },
    async refetchPage(options) {
      this.leaveOptions = { ...options }
      await this.fetchLeaveRequestsMain()
    },
    addPartButton(item) {
      return item.requestStatus === 'RAISED'
    }
  }
}
</script>

<style scoped>
.comment-cell {
  max-width: 150px;
}

.comment-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ellipsisClass {
  max-width: 140px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.v-data-table >>> .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none;
}
::v-deep .v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 10px;
  align-items: center;
}
.table-radius {
  border-radius: 0px !important;
}

.v-menu__content {
  border-radius: 10px;
}
.fixed-width-col {
  max-width: 250px;
  min-width: 250px;
  padding-top: 0px !important;
  padding-bottom: 0px !important;
}

.fixed-width-input {
  width: 100%;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Ensure the component can scroll if needed */
.page-container {
  max-height: 95vh;
  overflow-y: auto;
}
</style>
